import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import useUser from '../../hooks/useUser';
import useCheckoutRedirect from '../../hooks/useCheckoutRedirect';
import useCourseCatalog from '../../hooks/useCourseCatalog';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import Reveal from '../../components/course/Reveal';
import StarRating, { TRUSTPILOT_URL } from '../../components/course/StarRating';
import LogoRow from '../../components/course/LogoRow';
import FactStrip from '../../components/course/FactStrip';
import PlanSelector from '../../components/course/PlanSelector';
import PromoCountdown from '../../components/course/PromoCountdown';
import CurriculumAccordion from '../../components/course/CurriculumAccordion';
import PreviewVideo from '../../components/course/PreviewVideo';
import GuaranteeBlock from '../../components/course/GuaranteeBlock';
import ScrollProgress from '../../components/course/ScrollProgress';
import {
  ROB_STORY, SEO_TOPICS, NO_CODING_BADGE,
  FIRST_NIGHT_PROMISE, GUARANTEE_DAYS, COMPETITION, PRICES_VERIFIED_ON,
  WORTH_HEADING, WORTH_LEDE, WORTH_PILLARS,
} from '../../components/course/courseContent';
import './CoursePage.css';

const FIT_CALL_URL = 'https://calendly.com/lumiwealth/botspot-call';
export const SELF_PACED_PATH = '/courses/ai-trading-bootcamp/self-paced';

/**
 * Hero copy variants. Only the headline and subhead differ so the experiment has
 * exactly one variable. "Agentic trading" and "Claude Code" carry the searchable
 * demand (agentic trading 590/mo peaking at 4,400; claude code course 1,600/mo)
 * while "ai trading course" is effectively dead at 30/mo, so those terms live in
 * the copy rather than in the product name.
 */
export const HERO_VARIANTS = {
  control: {
    key: 'trading',
    h1: 'Trade With AI. Live, in 5 Weeks.',
    sub: 'Command Claude Code and Codex to research the market, verify what is actually true, and place reviewed trades through your own broker.',
  },
  variant: {
    key: 'agentic',
    h1: 'Put an AI Trading Desk to Work. You Make the Calls.',
    sub: 'Agentic trading, taught live. Use Claude Code and Codex to research, verify and execute, on rules you set and can check.',
  },
};

/**
 * Pain points, taken from what traders actually posted, not from personas.
 *
 * Sourced 2026-08-04 from the two highest-signal AI-trading threads of the
 * year and their comment trees. Full audit with scores and links:
 * MarketingManager/docs/campaigns/2026-08-04_ai-trading-pain-point-audit.md
 *
 *  - r/algotrading "Claude algo bot week 2, 100% wins" (536 up, 562 comments).
 *    The two highest-voted substantive replies both said the same thing:
 *    a good week proves nothing. "Five trades is not a sample size" (+20),
 *    "The real signal is the backtest, not the live week" (+46).
 *  - r/Daytrading "AI now saves me 2 hours every morning" (529 up). The pain
 *    being solved is stated outright: 1-2 hours of pre-market scanning.
 *  - Same thread, +12: "POC is 6785 it might say its 6780". The AI is wrong
 *    by just enough to be dangerous, and his fix is reading values aloud to it
 *    for ten minutes every morning.
 *  - Same thread, +72 on the original post: "They're saying you're going to
 *    lose it all."
 *
 * RULE: do not write a pain point here that you cannot trace to a real, dated,
 * upvoted thing a real person said. The earlier version was invented and Rob
 * called it out.
 */
const PAIN_POINTS = [
  {
    img: '/images/courses/problem-sample-size.webp',
    title: 'Five green days is not proof',
    body: 'Your AI had a good week. You still cannot tell whether that was an edge or a coin flip, and putting real size behind it is a guess.',
  },
  {
    img: '/images/courses/problem-close-enough.webp',
    title: 'It said 6785. It was 6780',
    body: 'Close enough to believe. Wrong enough to cost you. And nothing in the answer tells you which numbers it actually checked.',
  },
  {
    img: '/images/courses/problem-research-time.webp',
    title: 'Two hours before the bell. Every day',
    body: 'Scanning tickers, news and sentiment by hand, then still going in on a hunch because the reading never quite finished.',
  },
];

const LEARN_ITEMS = [
  'Set up Claude Code and Codex as a professional trading workspace',
  'Force an AI to cite its sources and catch it when it invents one',
  'Screen and analyse stocks with live market data instead of stale training data',
  'Read filings, earnings and news at machine speed without losing the plot',
  'Connect your real broker and place a reviewed trade through your AI',
  'Structure options trades with defined, deliberate risk',
  'Monitor positions and get told what changed and why it matters',
  'Automate only the parts that genuinely deserve automation',
];

const SKILLS = [
  'Agentic trading', 'Claude Code', 'Codex', 'Prompt verification', 'Broker APIs',
  'Options analysis', 'Risk review', 'Portfolio monitoring', 'MCP servers', 'TradingView workflows',
];

/**
 * What is included.
 *
 * Rendered as a list, image left and copy right, because a grid of tiles with
 * three-word captions told a buyer nothing. Rob's note (2026-08-04): every line
 * has to say what you actually get and where you get it, not a slogan.
 *
 * Wording rules learned the hard way:
 *   - "Every recording" meant nothing. Say what recordings, and for how long.
 *   - "Your setup reviewed" meant nothing. Say who reviews what, and when.
 *   - "Every prompt used, copy paste done" meant nothing. Say what the artefact
 *     actually is.
 */
const INCLUDED = [
  {
    img: '/images/courses/inc-recordings.webp',
    name: 'Five live sessions with Rob',
    detail:
      'Five weeks, one live session a week, ninety minutes each, taught by Rob and not by a stand-in. '
      + 'You can interrupt, ask questions and get answers on the spot, and most weeks somebody shares '
      + 'their screen and gets walked through their own setup.',
  },
  {
    img: '/images/courses/inc-recordings.webp',
    name: 'Every recording, kept for good',
    detail:
      'Each session is posted to your course portal within a day, and you keep access permanently. '
      + 'That includes recordings of the previous cohorts, so you can go back through everything that '
      + 'has already been taught, not just your own five weeks.',
  },
  {
    img: '/images/courses/inc-prompts.webp',
    name: 'The cohort trading competition',
    detail:
      'Everyone sets up a tracked account in week one and we follow it for the whole bootcamp. Each '
      + 'week you can see how the class is doing and where you sit. It is the fastest way to find out '
      + 'whether what you built actually works, and week five is spent going through the results.',
  },
  {
    img: '/images/courses/inc-prompts.webp',
    name: 'The full prompt and workflow pack',
    detail:
      'Every prompt, checklist and workflow used in class, in a document you can copy straight into '
      + 'your own agent, plus the slides. Not a summary written afterwards, the actual working '
      + 'material from the sessions.',
  },
  {
    img: '/images/courses/inc-prompts.webp',
    name: 'Your own setup reviewed on camera',
    detail:
      'Bring what you have built and Rob goes through it live with the class: what is wired up wrong, '
      + 'what to fix first, what to do next. Watching him do this for other people is often more useful '
      + 'than your own turn.',
  },
  {
    img: '/images/courses/inc-guarantee.webp',
    name: `${GUARANTEE_DAYS}-day money-back guarantee`,
    detail:
      `Attend, try it, and if it is not for you ask for a refund within ${GUARANTEE_DAYS} days and you `
      + 'get your money back. No forms and no argument. That window is longer than the bootcamp itself, '
      + 'on purpose.',
  },
];

/**
 * What you need.
 *
 * Rob's correction (2026-08-04): a brokerage account is NOT a prerequisite.
 * we set one up with you, and week one uses paper trading anyway. "Zero coding"
 * is not something you need either, it is a reassurance. The honest answer to
 * "what do I need" is a phone or a laptop, so the section says that and then
 * explains what we handle for you.
 */
const REQUIREMENTS_LEDE =
  'Almost nothing. This is the whole list, and we set the rest up with you in the first session.';

const REQUIREMENTS = [
  {
    img: '/images/courses/inc-prompts.webp',
    title: 'A laptop or a phone',
    detail:
      'Mac, Windows or Linux, and nothing powerful. Plenty of the work can be done from a phone. '
      + 'This is genuinely the only thing you have to bring.',
  },
  {
    img: '/images/courses/req-broker.webp',
    title: 'A brokerage account, or ten minutes to open one',
    detail:
      'If you already trade with Schwab, Alpaca, Tradier, Interactive Brokers, Coinbase or Kraken, we '
      + 'connect it in week one. If you do not have one, we walk you through opening it, and you start '
      + 'on paper trading with fake money either way.',
  },
  {
    img: '/images/courses/inc-prompts.webp',
    title: 'No coding, and no trading experience',
    detail:
      'You direct the AI in plain English. People arrive having never opened a terminal and leave with '
      + 'a working setup. If you can describe what you want, you can do this.',
  },
];

/**
 * FAQ.
 *
 * Deliberately long. This section does double duty: it handles the real
 * objections that stop a purchase, and it is the part of the page that AI
 * answer engines quote when somebody asks "what is agentic trading" or "is
 * there a course for trading with Claude Code". So the answers are written as
 * complete, standalone statements rather than as fragments that only make
 * sense next to the question.
 *
 * Rob's direction (2026-08-04): push the agentic-trading narrative hard. We
 * used to teach people to build strategies. Teaching people to actually trade,
 * with AI doing the work, is more interesting and is what is taking off. Do
 * not answer the trading-bots question with a flat "no". It is partly yes.
 */
const FAQS = [
  {
    q: 'Do I need to know how to code?',
    a: 'No. You describe what you want in plain English and review what comes back. We set your workspace up together in week one, from scratch, and plenty of people arrive having never opened a terminal.',
  },
  {
    q: 'What is agentic trading?',
    a: 'It is the difference between asking an AI a question and giving an AI a job. An agent goes and does the work: it pulls live market data, reads the filings and the news, checks its own claims against sources, lays out the case for and against, and places the order. You decide how much rope it gets. Some of it you approve trade by trade, and some of it you let run while you sleep. That loop is what this bootcamp teaches, and it is the thing that has changed fastest in the last year.',
  },
  {
    q: 'Is this a course about building trading bots?',
    a: 'Partly yes, and automation is very much the goal. An agent that keeps working while you sleep is a large part of the point. What changed is the order we teach it in. We used to start by building a bot. Now you learn to run the job yourself with an agent first, so that when you do hand something over to run unattended, you understand exactly what it is doing and why. You also do not have to build everything from scratch: the BotSpot marketplace has finished strategies you can run, and this course is what lets you judge whether one of them is any good.',
  },
  {
    q: 'How much time does this take?',
    a: 'The live session is usually one to two hours depending on how much the room wants to dig into, plus one to two hours of your own work in the week. Call it two to four hours a week. Fair warning: most people spend more, not because we ask them to, but because once you see what this can do you start wanting to try things.',
  },
  {
    q: 'What actually happens in a live session?',
    a: 'A lot of demoing. Rob builds live, you build live, and people bring what they made and show it. You will see other people\'s setups, other people\'s trades and other people\'s accounts, which is the part you cannot get from a recorded course. It is interactive by design, so the pace follows the questions in the room.',
  },
  {
    q: 'What is the cohort trading competition?',
    a: `${COMPETITION.body} You can opt out and stay off the board if you would rather keep your numbers private.`,
  },
  {
    q: 'What if I miss a session?',
    a: 'Every session is recorded and posted to your course portal within a day, and you keep access permanently. Bring your question to the next live session and it gets answered on camera.',
  },
  {
    q: 'Which brokers can I use?',
    a: 'Schwab, Alpaca, Tradier, Interactive Brokers, Coinbase and Kraken are all covered. We connect at least one of them live in class, and you will be trading through your own account, not a simulator.',
  },
  {
    q: 'Do I need a big account to make this worth it?',
    a: 'No, and you should not start with one. The skills are the same whether you are running four figures or seven. Several people go through the whole bootcamp on a small account deliberately, because the point of the five weeks is to build a process you trust before you size up.',
  },
  {
    q: 'How is this different from just using ChatGPT myself?',
    a: 'Mostly in whether you can trust the output. An unassisted chatbot will hand you a confident number pulled from stale training data and cite nothing, and you will not be able to tell the good answers from the bad ones. The whole method here is about forcing the AI to work from live data, show its sources, and be checkable, and then wiring it to your broker so the verified work actually turns into an order.',
  },
  {
    q: 'What happens right after I buy?',
    a: 'You get immediate access to the course portal with the session calendar, and an email with everything you need to have ready before week one.',
  },
  {
    q: 'What if it is not for me?',
    a: `Ask for a refund within ${GUARANTEE_DAYS} days and you get your money back. No forms and no argument. That window is longer than the bootcamp itself, on purpose.`,
  },
  {
    q: 'Can I talk to someone before buying?',
    a: 'Yes. Book a call with Rob and ask whatever you need to ask. If it is not a fit he will tell you so.',
  },
];

/**
 * Real Trustpilot review screenshots, captured from the public profile.
 *
 * Deliberately shown as screenshots rather than restyled quotes: the Trustpilot
 * chrome (green stars, reviewer name and country, review count, date, the
 * "Verified" and "Unprompted review" badges) is the thing that makes them
 * believable. Restyling them into our own cards throws all of that away and
 * makes real reviews look invented.
 */
const REVIEW_SHOTS = [
  { src: '/images/reviews/tp-0.jpg', alt: 'Trustpilot review by Mauricio Rascon, five stars', tilt: -1.6 },
  { src: '/images/reviews/tp-3.jpg', alt: 'Trustpilot review by Worth, five stars', tilt: 1.2 },
  { src: '/images/reviews/tp-2.jpg', alt: 'Verified Trustpilot review by Daniel Ogola, five stars', tilt: -0.9 },
  { src: '/images/reviews/tp-6.jpg', alt: 'Trustpilot review by Charlie Murphy, five stars', tilt: 1.7 },
  { src: '/images/reviews/tp-4.jpg', alt: 'Trustpilot review by Robert Sanon, five stars', tilt: -1.3 },
  { src: '/images/reviews/tp-5.jpg', alt: 'Trustpilot review by Larry Ayala, five stars', tilt: 1.0 },
];


/**
 * Repeatable inline CTA.
 *
 * Rob's note (2026-08-04): the page went long stretches with no way to act.
 * These land after the sections that do the persuading: the problem, the
 * curriculum and the reviews. A decision made mid-page can be acted on
 * without scrolling to find a button.
 */
const CtaBand = ({ line, label, onEnroll, disabled, testId }) => (
  <Reveal>
    <div className="cp-cta-band">
      <p>{line}</p>
      <button type="button" className="cp-btn-primary" data-testid={testId}
        onClick={onEnroll} disabled={disabled}>
        {label}
      </button>
    </div>
  </Reveal>
);

const CoursePage = ({ variantKey = 'control' }) => {
  const { user } = useUser();
  const hero = HERO_VARIANTS[variantKey] || HERO_VARIANTS.control;
  const { startCheckout } = useCheckoutRedirect('course_sales');
  const {
    isLoading, plans, upfrontPlan, selfPaced, sessions, cohortStart,
    isEnrollable, formatSessionDate, promoEndsAt, promoName, hasActivePromo,
    pricingError,
  } = useCourseCatalog();

  /* INCLUDED already leads with the live sessions and describes them properly.
     A second, three-word "5 live sessions / Live with Rob" entry used to be
     prepended here and rendered as a duplicate first row. */
  const includedItems = INCLUDED;

  const [selectedPlanKey, setSelectedPlanKey] = useState(null);
  useEffect(() => {
    if (!selectedPlanKey && upfrontPlan) setSelectedPlanKey(upfrontPlan.planKey);
  }, [upfrontPlan, selectedPlanKey]);

  const selectedPlan = plans.find((p) => p.planKey === selectedPlanKey) || upfrontPlan;

  const track = useCallback((event, extra = {}) => {
    pushEvent({
      event, source: window.location.pathname, variant: hero.key,
      userId: user?.id || user?.sub, email: user?.email, ...extra,
    });
  }, [hero.key, user]);

  const enroll = useCallback((ctaLocation) => {
    const planKey = selectedPlan?.planKey || 'bootcamp-upfront';
    track(ANALYTICS_EVENTS.BOOTCAMP_CHECKOUT_STARTED, { cta_location: ctaLocation, planKey });
    return startCheckout({
      products: [{ productSlug: 'ai-bootcamp', planKey }],
      flowContext: 'course_sales',
      // Land the buyer inside the product with the first-run panel, not on a
      // dead-end thank-you page.
      metadata: {
        origin: {
          flow: 'course_sales', ctaLocation, variant: hero.key,
          returnUrl: '/ai-bot-builder-bootcamp/watch?welcome=1',
        },
      },
      contact: { email: user?.email || '', name: user?.name || '' },
    });
  }, [startCheckout, track, selectedPlan, hero.key, user]);

  const startLabel = useMemo(
    () => (isEnrollable && cohortStart ? formatSessionDate(cohortStart) : null),
    [isEnrollable, cohortStart, formatSessionDate]
  );

  /* Illustrated, not icon-fonted. Font Awesome glyphs read as cheap at this
     size next to the rest of the page. */
  /* Designed cards use inspected no-person artwork. The live-session fact
     falls back to accessible DOM text until approved replacement art exists.
     Rob set the weekly commitment to a flat "3 hours a week" (2026-08-04).
     The old "2-4" read as hedging. */
  const facts = useMemo(() => ([
    {
      value: '5', label: 'Live sessions with Rob',
      card: null,
      blurb: 'One live session a week for five straight weeks, ninety minutes each, taught by Rob. Every one is recorded and yours to keep.',
    },
    {
      value: '3 hours', label: 'A week',
      card: '/images/courses/fact-card-hours.webp',
      blurb: 'The session itself runs one to two hours, and we suggest another one to two hours of your own work between classes. Nothing is graded.',
    },
    {
      value: `${GUARANTEE_DAYS} days`, label: 'Money-back guarantee',
      card: '/images/courses/fact-card-guarantee.webp',
      blurb: `Ask for a refund within ${GUARANTEE_DAYS} days and you get your money back. No forms, no argument. That is longer than the bootcamp itself.`,
    },
  ]), []);


  const enrollmentUnavailable = isLoading || Boolean(pricingError) || !isEnrollable;

  /* Button labels stay short enough to sit on one line at every breakpoint.
     "Enroll, starts Wednesday, October 7" wrapped to two lines in the hero, in
     the sticky bar and in every CTA band. The date belongs beside the button,
     not inside it. */
  const ctaLabel = enrollmentUnavailable ? 'Enrollment unavailable' : 'Enroll now';

  return (
    <div className="course-page" data-testid="course-page" data-variant={hero.key}>
      <ScrollProgress />
      <Helmet>
        <title>{`AI Trading Bootcamp | ${hero.h1} | BotSpot`}</title>
        <meta name="description" content={hero.sub} />
        {/* FAQPage schema. The FAQ is the part of this page that answer engines
            quote, so it is emitted as structured data rather than left as prose
            they have to guess at. */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          })}
        </script>
      </Helmet>

      {/* Promo bar: only ever renders from a real, unexpired promotion */}
      {hasActivePromo && promoEndsAt && (
        <div className="cp-promobar" data-testid="promo-bar">
          <span>{promoName || 'Limited-time pricing'}</span>
          <PromoCountdown endsAt={promoEndsAt} label="Ends in" />
        </div>
      )}

      {/* 1. HERO */}
      <header className="cp-hero">
        <div className="cp-hero-copy">
          <Reveal><p className="cp-eyebrow">Agentic Trading Bootcamp · Live cohort</p></Reveal>
          <Reveal delay={60}><h1>{hero.h1}</h1></Reveal>
          <Reveal delay={120}><p className="cp-sub">{hero.sub}</p></Reveal>
          <Reveal delay={180}><div className="cp-hero-rating"><StarRating rating={4.6} /></div></Reveal>

          <Reveal delay={240}>
            {/* Never shows an invented price: if the catalog is unreadable we say so. */}
            {pricingError ? (
              <p className="cp-price-error" data-testid="pricing-error" role="alert">
                Pricing is temporarily unavailable. Please refresh, or contact us and we will help you enroll.
              </p>
            ) : (
              <div className="cp-hero-price" data-testid="hero-price">
                {selectedPlan?.hasDiscount && selectedPlan.originalDisplay ? (
                  <span className="cp-price-was">{selectedPlan.originalDisplay}</span>
                ) : null}
                <span className="cp-price-now">{selectedPlan?.display || ''}</span>
                {selectedPlan?.installments > 1 ? <span className="cp-price-unit">/mo</span> : null}
                {selectedPlan?.savingsDisplay ? (
                  <span className="cp-price-save">Save {selectedPlan.savingsDisplay}</span>
                ) : null}
              </div>
            )}
          </Reveal>

          <Reveal delay={300}>
            <div className="cp-hero-cta">
              <button type="button" className="cp-btn-primary" data-testid="enroll-hero"
                onClick={() => enroll('hero')} disabled={enrollmentUnavailable}>
                {ctaLabel}
              </button>
              <a className="cp-btn-ghost" href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer"
                onClick={() => track(ANALYTICS_EVENTS.BOOTCAMP_PATH_SELECTED, { path: 'fit_call', cta_location: 'hero' })}>
                Talk to the instructor
              </a>
            </div>
          </Reveal>
          {startLabel && !enrollmentUnavailable ? (
            <Reveal delay={310}>
              <p className="cp-hero-when">Next cohort starts {startLabel}</p>
            </Reveal>
          ) : null}

          {/* The live cohort is only sellable once the catalog has scheduled
              sessions. Until then the enrol button is correctly disabled, but a
              disabled button on its own is a dead end and a dead end costs the
              sale. Always leave a way to buy something or to talk to us. */}
          {enrollmentUnavailable && !isLoading ? (
            <Reveal delay={315}>
              <p className="cp-enroll-fallback" data-testid="enroll-fallback">
                Dates for the next live cohort are not open yet.{' '}
                {selfPaced ? (
                  <>
                    <Link to={SELF_PACED_PATH}>Start self-paced today for {selfPaced.display}</Link>
                    {', or '}
                  </>
                ) : null}
                <a href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer">
                  book a call with Rob
                </a>{' '}
                and he will tell you when the next one runs.
              </p>
            </Reveal>
          ) : null}
          <Reveal delay={330}>
            <div className="cp-hero-assurance">
              <p className="cp-firstnight">{FIRST_NIGHT_PROMISE}</p>
              <p className="ck-nocode">{NO_CODING_BADGE}</p>
            </div>
          </Reveal>
          {selfPaced && (
            <Reveal delay={350}>
              <p className="cp-selfpaced">
                Cannot make the live sessions?{' '}
                <Link to={SELF_PACED_PATH} onClick={() => track(ANALYTICS_EVENTS.BOOTCAMP_PATH_SELECTED, { path: 'self_paced' })}>
                  Self-paced from {selfPaced.display}
                </Link>
              </p>
            </Reveal>
          )}
        </div>

        <Reveal delay={160} className="cp-hero-art">
          <img src="/images/courses/ai-trading-workspace-hero.webp"
            alt="An AI trading workspace: assistant research on the left, price chart and a trade review card on the right"
            width="1280" height="714" loading="eager" />
        </Reveal>
      </header>

      <LogoRow />
      <FactStrip facts={facts} />

      {/* Free class preview: the strongest proof asset we own. */}
      <PreviewVideo />

      {/* Story before offer (Brunson). Sits beside Rob's photo and is kept short
          on purpose: the long version lives in the preview video above. */}
      <section className="cp-section cp-story">
        <Reveal>
          <div className="cp-story-body">
            <img src="/images/rob.jpg" alt="Rob Grzesik" width="150" height="150" decoding="async" />
            <div>
              <h2>{ROB_STORY.heading}</h2>
              {/* Render every paragraph. This used to hardcode indices 0 and 2,
                  which silently dropped the paragraph that turns the 2008 blow-up
                  into a track record, which is the whole point of telling it. */}
              {ROB_STORY.paragraphs.map((para) => <p key={para.slice(0, 40)}>{para}</p>)}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="cp-section cp-problem">
        <Reveal><h2>If you have tried this already, you know the problem</h2></Reveal>
        <div className="cp-problem-grid">
          {PAIN_POINTS.map((p, i) => (
            <Reveal key={p.title} delay={i * 90}>
              {/* The banner carries its own headline, so the card is the image.
                  Alt text preserves the message for screen readers. */}
              <img className="cp-problem-img" src={p.img}
                   alt={`${p.title} ${p.body}`} loading="lazy" width="1280" height="714" />
            </Reveal>
          ))}
        </div>
        <Reveal delay={280}>
          <p className="cp-turn">
            The tools are good enough now. What is missing is the workflow: how to make AI show its
            evidence, connect it to your real accounts, and keep every decision under your control.
          </p>
        </Reveal>
      </section>

      <CtaBand
        line="That is the problem this fixes. Five weeks, live."
        label={ctaLabel} testId="enroll-problem"
        onEnroll={() => enroll('after_problem')} disabled={enrollmentUnavailable}
      />

      <section className="cp-section cp-learn">
        <Reveal><h2>What you will learn</h2></Reveal>
        <ul className="cp-learn-grid">
          {LEARN_ITEMS.map((item, i) => <Reveal as="li" key={item} delay={i * 45}>{item}</Reveal>)}
        </ul>
      </section>

      <section className="cp-section cp-skills">
        <Reveal><h2>Skills you will gain</h2></Reveal>
        <Reveal delay={80}>
          <ul className="cp-skill-pills">{SKILLS.map((s) => <li key={s}>{s}</li>)}</ul>
        </Reveal>
      </section>

      <section className="cp-section cp-curriculum" id="curriculum" data-testid="curriculum">
        <Reveal><h2>The curriculum</h2></Reveal>
        {sessions.length ? (
          <CurriculumAccordion sessions={sessions} formatDate={formatSessionDate} />
        ) : (
          <Reveal>
            <p className="cp-muted" data-testid="curriculum-unavailable" role="status">
              The next cohort schedule is not available yet. Enrollment will open when the live
              session dates are published.
            </p>
          </Reveal>
        )}
      </section>

      <CtaBand
        line="Five weeks, five live sessions, and every recording is yours."
        label={ctaLabel} testId="enroll-curriculum"
        onEnroll={() => enroll('after_curriculum')} disabled={enrollmentUnavailable}
      />

      {/* A list, not a tile grid. Tiles with three-word captions did not tell a
          buyer what they were getting. */}
      <section className="cp-section cp-included">
        <Reveal><h2>Everything you get</h2></Reveal>
        <ul className="cp-included-list">
          {includedItems.map((item, i) => (
            <Reveal as="li" key={item.name} delay={i * 60}>
              <img className="cp-included-img" src={item.img} alt="" loading="lazy" width="512" height="512" />
              <div>
                <h3>{item.name}</h3>
                <p>{item.detail}</p>
              </div>
            </Reveal>
          ))}
        </ul>
      </section>

      <section className="cp-section cp-requirements">
        <Reveal><h2>What you need to bring</h2></Reveal>
        <Reveal delay={60}><p className="cp-req-lede">{REQUIREMENTS_LEDE}</p></Reveal>
        <ul className="cp-req-grid">
          {REQUIREMENTS.map((r, i) => (
            <Reveal as="li" key={r.title} delay={i * 80}>
              <img className="cp-req-img" src={r.img} alt="" loading="lazy" width="512" height="512" />
              <div>
                <strong>{r.title}</strong>
                <span className="cp-req-detail">{r.detail}</span>
              </div>
            </Reveal>
          ))}
        </ul>
      </section>

      {/* Qualification, written from what people actually post rather than
          from guesses. Each disqualifier below traces to a heavily upvoted
          thread. See the pain-point audit in MarketingManager. Turning the
          wrong buyer away is worth more than the sale: refunds and unhappy
          students in a five-week cohort cost more than they bring in. */}
      {/* Qualification by WORLDVIEW, not by capability.

          An earlier version of this list disqualified our own product. It said
          we never give picks or signals. The Marketplace Strategy Subscription
          is a live product with three plans. It said wanting a bot that runs
          while you sleep is the wrong reason to be here. Overnight automation
          is a large part of the point. And it said wanting a finished system is
          disqualifying. The marketplace exists to hand people finished
          systems. Rob caught all three on 2026-08-04.

          The only person genuinely wrong for this course is someone who does
          not believe in agentic trading. Disqualify on that and nothing else,
          and never write a line here that contradicts something we sell. */}
      <section className="cp-section cp-fit" data-testid="fit-section">
        <Reveal><h2>Who this is really for</h2></Reveal>
        <div className="cp-fit-cols">
          <Reveal>
            <div className="cp-fit-col is-yes">
              <h3><span aria-hidden="true">✓</span> This is for you if</h3>
              <ul>
                <li>You think agents are about to change how trading works, and you would rather be early than on time</li>
                <li>You already invest or trade, and the research is eating hours you do not have</li>
                <li>You want the AI doing the work, including overnight, with you setting the rules it plays by</li>
                <li>You want to see the evidence behind a call before you act on it, not just take it on faith</li>
                <li>You would rather describe what you want in plain English than spend a weekend writing code</li>
              </ul>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="cp-fit-col is-no">
              <h3><span aria-hidden="true">✕</span> This is not for you if</h3>
              <ul>
                <li>You think AI is a fad and agentic trading is a gimmick. Five weeks of this will only annoy you</li>
                <li>You want the artisanal version, everything by hand and by eye. That is a real way to trade. It is not this one</li>
                <li>You would rather wing it. If nothing needs checking or backing up, the whole method is wasted on you</li>
                <li>You enjoy the coding more than the trading. We are deliberately going the other way</li>
                <li>You want a 1000% month. Rob can show you how to gamble. He is not going to pretend it is a strategy</li>
              </ul>
            </div>
          </Reveal>
        </div>
        <Reveal delay={180}>
          <p className="cp-fit-foot">
            If the right-hand column is you, keep your money. You would not enjoy this.{' '}
            <a href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer">
              Not sure which column you are in? Ask Rob.
            </a>
          </p>
        </Reveal>
      </section>

      {/* Lumibot is mentioned here and nowhere else on the page. */}
      <section className="cp-section cp-instructor">
        <Reveal><h2>Who teaches it</h2></Reveal>
        <Reveal delay={80}>
          <div className="cp-instructor-body">
            {/* Not lazy: this is a trust element and must always render. */}
            <img src="/images/rob.jpg" alt="Rob Grzesik" width="200" height="200" decoding="async" />
            <div>
              <h3>Rob Grzesik</h3>
              {/* Wording follows MarketingManager/docs/ROB-PROFILE-POSITIONING.md, which
                  lists claims that must NOT be made (co-founder of Voyager, $1B AUM,
                  $70B AUM at Greystone). Do not embellish these lines. */}
              <p>
                25+ years of software engineering and 15+ years in financial technology. As an
                early-stage employee at Voyager Digital, Rob built the core trading and brokerage
                infrastructure from scratch and grew the engineering team, at a company that reached
                a $1B+ peak market cap. He was previously VP of Fintech at Greystone and VP of
                Software Engineering at Perch, and has hired and managed 100+ engineers.
              </p>
              <p>
                He created Lumibot, the open-source algorithmic trading framework with 2,000+ GitHub
                stars used by developers worldwide, has personally built over 1,000 trading bots, and
                holds a Master of Finance from the Schulich School of Business at York University.
              </p>
              <p>He has taught algorithmic trading to thousands of students, and he teaches every session of this bootcamp himself.</p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="cp-section cp-reviews">
        <Reveal>
          <div className="cp-reviews-head">
            <h2>What students say</h2>
            <StarRating rating={4.6} compact />
          </div>
        </Reveal>
        <div className="cp-reviews-wall">
          {REVIEW_SHOTS.map((shot, i) => (
            <Reveal key={shot.src} delay={i * 70} className="cp-review-shot">
              <a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer"
                 style={{ '--tilt': `${shot.tilt}deg` }}>
                <img src={shot.src} alt={shot.alt} loading="lazy" />
              </a>
            </Reveal>
          ))}
        </div>
        <Reveal delay={420}>
          <p className="cp-reviews-more">
            <a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer">
              Read all reviews on Trustpilot
            </a>
          </p>
        </Reveal>
      </section>

      {/* This stack only displays a price supplied by the live catalog. */}
      {/* WORTH, not cost-substitution. Every figure here is about the buyer's
          own time, skills or process, is published and linked, and none of it
          is a return claim. The old version priced this against five older
          Lumiwealth courses that are not included in the bootcamp. That was
          misleading and Rob killed it. Do not bring it back. */}
      <CtaBand
        line="Join them in the next cohort."
        label={ctaLabel} testId="enroll-reviews"
        onEnroll={() => enroll('after_reviews')} disabled={enrollmentUnavailable}
      />

      <section className="cp-section cp-worth" data-testid="value-section">
        <Reveal><h2>{WORTH_HEADING}</h2></Reveal>
        <Reveal delay={60}><p className="cp-worth-lede">{WORTH_LEDE}</p></Reveal>

        <div className="cp-worth-grid">
          {WORTH_PILLARS.map((pillar, i) => (
            <Reveal key={pillar.key} delay={100 + i * 80}>
              <article className="cp-worth-card">
                <div className="cp-worth-media">
                  <img src={pillar.img} alt="" loading="lazy" />
                </div>
                <div className="cp-worth-body">
                  <p className="cp-worth-stat">
                    <strong>{pillar.stat}</strong>
                    <span>{pillar.statLabel}</span>
                  </p>
                  <h3>{pillar.heading}</h3>
                  <p className="cp-worth-text">{pillar.body}</p>
                  {pillar.source ? (
                    <p className="cp-worth-source">
                      {pillar.href ? (
                        <a href={pillar.href} target="_blank" rel="noopener noreferrer">{pillar.source}</a>
                      ) : pillar.source}
                    </p>
                  ) : null}
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={500}>
          <div className="cp-cta-band">
            <p>The bootcamp is {upfrontPlan?.display || 'one price'}, once.</p>
            <button type="button" className="cp-btn-primary" data-testid="enroll-worth"
              onClick={() => enroll('value_stack')} disabled={enrollmentUnavailable}>
              {ctaLabel}
            </button>
          </div>
        </Reveal>
      </section>

      <section className="cp-section cp-pricing" id="pricing" data-testid="pricing">
        <Reveal><h2>Choose how you pay</h2></Reveal>
        {isLoading ? (
          <p className="cp-muted">Loading current pricing...</p>
        ) : pricingError ? (
          <Reveal>
            <div className="cp-pricing-card" data-testid="pricing-error-card">
              <p className="cp-price-error" role="alert">
                We could not load current pricing. Rather than show you a price that might be wrong,
                we would rather say so. Please refresh, or contact us and we will get you enrolled.
              </p>
              <a className="cp-btn-primary" href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer">
                Talk to the instructor
              </a>
              <GuaranteeBlock compact />
            </div>
          </Reveal>
        ) : (
          <Reveal delay={80}>
            <div className="cp-pricing-card">
              {hasActivePromo && promoEndsAt ? (
                <div className="cp-pricing-promo">
                  <strong>{promoName || 'Limited-time pricing'}</strong>
                  <PromoCountdown endsAt={promoEndsAt} label="Ends in" />
                </div>
              ) : null}

              <PlanSelector plans={plans} selectedKey={selectedPlanKey} onSelect={setSelectedPlanKey} />

              <button type="button" className="cp-btn-primary cp-btn-block" data-testid="enroll-pricing"
                onClick={() => enroll('pricing')} disabled={enrollmentUnavailable}>
                {ctaLabel}
              </button>
              <p className="ck-nocode cp-nocode-cta">{NO_CODING_BADGE}</p>
              {/* Risk reversal sits with the payment decision, not three
                  screens away from it. */}
              <GuaranteeBlock compact />
              <p className="cp-pricing-links">
                {selfPaced ? <Link to={SELF_PACED_PATH}>Self-paced from {selfPaced.display}</Link> : null}
                <a href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer">Book a call with Rob</a>
              </p>
            </div>
          </Reveal>
        )}
      </section>

      <section className="cp-section cp-faq">
        <Reveal><h2>Questions people ask</h2></Reveal>
        <dl>
          {FAQS.map((f, i) => (
            <Reveal as="div" key={f.q} delay={i * 40}><dt>{f.q}</dt><dd>{f.a}</dd></Reveal>
          ))}
        </dl>
      </section>

      <CtaBand
        line="Still have a question? Ask Rob on a call, or just start."
        label={ctaLabel} testId="enroll-faq"
        onEnroll={() => enroll('after_faq')} disabled={enrollmentUnavailable}
      />

      <section className="cp-section cp-final" style={{
        backgroundImage:
          'linear-gradient(rgba(6,11,22,0.72), rgba(6,11,22,0.88)), '
          + 'url("/images/courses/final-cta-bg.webp")',
      }}>
        <Reveal>
          <h2>Five weeks from now, you will have done this</h2>
          <p>
            {startLabel
              ? `The next cohort starts ${startLabel}. Your workspace is running on night one.`
              : 'Your workspace is running on night one, and every session is yours to keep.'}
          </p>
          <div className="cp-final-actions">
            <button type="button" className="cp-btn-primary" data-testid="enroll-final"
              onClick={() => enroll('final')} disabled={enrollmentUnavailable}>{ctaLabel}</button>
            <a className="cp-btn-ghost" href={FIT_CALL_URL} target="_blank" rel="noopener noreferrer">
              Talk to the instructor
            </a>
          </div>
          <p className="ck-nocode cp-nocode-cta">{NO_CODING_BADGE}</p>
        </Reveal>
      </section>

      <div className="cp-sticky">
        <span className="cp-sticky-meta">
          <span className="cp-sticky-course">AI Trading Bootcamp</span>
          {startLabel ? <span className="cp-sticky-when">Starts {startLabel}</span> : null}
        </span>
        <span>
          {pricingError || !selectedPlan ? 'Pricing unavailable' : (
            `${selectedPlan.display}${selectedPlan.installments > 1
              ? `/mo · ${selectedPlan.totalDisplay} total` : ''}`
          )}
        </span>
        <button type="button" data-testid="enroll-sticky"
          onClick={() => enroll('sticky')} disabled={enrollmentUnavailable}>
          {ctaLabel}
        </button>
      </div>

      <section className="cp-section cp-topics-section">
        <Reveal><h2>Topics covered</h2></Reveal>
        <Reveal delay={60}>
          <ul className="ck-topics">{SEO_TOPICS.map((t) => <li key={t}>{t}</li>)}</ul>
        </Reveal>
      </section>

      <p className="cp-disclaimer">
        BotSpot is not an investment advisor. This is educational material about using AI tools in your
        own research and trading process. Nothing here is a recommendation to buy or sell any security,
        and no outcome or return is promised.
      </p>
    </div>
  );
};

export default CoursePage;
